import React, { useCallback, useEffect, useState } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { changeUserProfileRating } from 'store/user/actions';
import { ParentalControlRating } from 'store/user/reducer.types';

import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import { isWeb } from 'utils/responsiveness';

import WebHeader from 'screens/Settings/components/WebHeader';

import DialogBody from 'components/Dialog/components/DialogBody';

import SelectAgeRating from '../components/SelectAgeRating';
import { nullFunction } from '../utils';
import { adultProfileRating, kidProfileRating } from './constants';
import styles from './styles';

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.PROFILE_AGE_RATING
    >;
    route: RouteProp<RouteNavigationParams, SCREEN.PROFILE_AGE_RATING>;
};

const {
    PROFILE: { PROFILE_AGE_RATING_OVERLAY_TITLE },
} = LANGUAGE_KEYS;

const ProfileAgeRating = ({
    navigation,
    route: {
        params: { isChildModeEnabled, ageRating },
    },
}: Props) => {
    const [isChildProfile] = useState(
        typeof isChildModeEnabled === 'string'
            ? JSON.parse(isChildModeEnabled)
            : isChildModeEnabled,
    );
    const [data, setData] = useState(
        isChildProfile ? kidProfileRating : adultProfileRating,
    );

    useHeaderConfig({});

    const { background } = useSelector(selectLayoutConfig);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    useEffect(() => {
        setData((prevData) =>
            prevData.map((item) => ({
                ...item,
                isSelected: ageRating.key === item.key,
            })),
        );
    }, [ageRating]);

    const handleSelectAgeRating = useCallback(
        (item: {
                label: string;
                key: ParentalControlRating;
                isSelected: boolean;
            }) =>
            () => {
                dispatch(
                    changeUserProfileRating({
                        rating: item.key,
                        key: item.key,
                    }),
                );

                navigation.goBack();
            },
        [dispatch],
    );

    const renderDescription = useCallback(() => {
        return (
            <SelectAgeRating
                onSelectAgeRating={handleSelectAgeRating}
                data={data}
            />
        );
    }, [isChildProfile, handleSelectAgeRating, data]);

    return (
        <View style={[styles.container, { backgroundColor: background }]}>
            {isWeb && (
                <WebHeader
                    title={t(PROFILE_AGE_RATING_OVERLAY_TITLE)}
                    style={{ backgroundColor: background }}
                    disableFocusOnMount
                />
            )}
            <DialogBody
                title=""
                style={{ backgroundColor: background }}
                DescriptionComponent={renderDescription}
                handleActionButtonPress={nullFunction}
            />
        </View>
    );
};

export default ProfileAgeRating;
